/**
 * Transform peristiwa kependudukan: kelahiran, kematian, dan migrasi.
 *
 * Tiga endpoint sumber (`kesehatan/kelahiran`, `kesehatan/kematian`,
 * `kependudukan/migrasipenduduk`) mengembalikan **seluruh riwayat**, bukan hanya
 * tahun berjalan. Menghitung panjang array mentah karenanya menjumlahkan semua
 * tahun — itulah yang membuat KPI berlabel "Tahun ini" menampilkan 5 kelahiran
 * padahal tahun berjalan hanya punya 1.
 *
 * Helper ini memfilter per tahun sebelum menghitung, mengikuti pola yang sudah
 * dipakai `./age.ts` dan `./religion.ts`. Dipakai bersama oleh halaman dashboard
 * dan builder video wall supaya keduanya tidak menyimpang.
 */

import { APP_TIMEZONE_OFFSET_MINUTES } from "@/config/timezone";

export interface RawEventRow {
	tanggal?: unknown;
	tgl?: unknown;
	date?: unknown;
	/** Hanya ada di baris migrasi: MASUK / KELUAR. */
	jenis?: unknown;
	type?: unknown;
	arah?: unknown;
}

const MS_PER_MINUTE = 60_000;

function asArray<T>(raw: unknown): T[] {
	return Array.isArray(raw) ? (raw as T[]) : [];
}

function firstDefined(...vals: unknown[]): unknown {
	return vals.find((v) => v !== undefined && v !== null);
}

/**
 * Tahun sebuah peristiwa menurut zona waktu desa (WITA), bukan UTC.
 *
 * Tanggal disimpan UTC; peristiwa pada 31 Des pukul 17:00 UTC sudah masuk
 * 1 Januari di Bali. Membaca tahun langsung dari UTC akan menaruhnya di tahun
 * yang salah bagi perangkat desa yang melihatnya.
 */
export function rowEventYear(row: RawEventRow): number | null {
	const raw = firstDefined(row.tanggal, row.tgl, row.date);
	if (raw === undefined) return null;
	const parsed = new Date(raw as string | number | Date);
	const ms = parsed.getTime();
	if (!Number.isFinite(ms)) return null;
	return new Date(
		ms + APP_TIMEZONE_OFFSET_MINUTES * MS_PER_MINUTE,
	).getUTCFullYear();
}

/** Tahun berjalan menurut zona waktu desa. `now` disuntik agar bisa diuji. */
export function currentEventYear(now: Date = new Date()): number {
	return new Date(
		now.getTime() + APP_TIMEZONE_OFFSET_MINUTES * MS_PER_MINUTE,
	).getUTCFullYear();
}

/** Daftar tahun unik yang punya peristiwa, urut menurun (terbaru dulu). */
export function eventYears(raw: unknown): number[] {
	const years = new Set<number>();
	for (const row of asArray<RawEventRow>(raw)) {
		const y = rowEventYear(row);
		if (y !== null) years.add(y);
	}
	return [...years].sort((a, b) => b - a);
}

/**
 * Jumlah peristiwa pada satu tahun.
 *
 * @param tahun `null`/undefined = hitung semua tahun (perilaku lama).
 *   Baris tanpa tanggal yang terbaca tidak diikutkan saat tahun ditentukan —
 *   lebih baik kurang menghitung daripada menaruhnya di tahun yang salah.
 */
export function countEventsInYear(raw: unknown, tahun?: number | null): number {
	const rows = asArray<RawEventRow>(raw);
	if (tahun == null) return rows.length;
	return rows.filter((row) => rowEventYear(row) === tahun).length;
}

function direction(row: RawEventRow): string {
	return String(
		firstDefined(row.jenis, row.type, row.arah) ?? "",
	).toLowerCase();
}

export function isMoveIn(row: RawEventRow): boolean {
	const d = direction(row);
	return d === "masuk" || d === "in";
}

export function isMoveOut(row: RawEventRow): boolean {
	const d = direction(row);
	return d === "keluar" || d === "out";
}

export interface MigrationCounts {
	moveIn: number;
	moveOut: number;
}

/** Pindah masuk & keluar pada satu tahun; `tahun` null = semua tahun. */
export function countMigrationInYear(
	raw: unknown,
	tahun?: number | null,
): MigrationCounts {
	const rows = asArray<RawEventRow>(raw).filter(
		(row) => tahun == null || rowEventYear(row) === tahun,
	);
	return {
		moveIn: rows.filter(isMoveIn).length,
		moveOut: rows.filter(isMoveOut).length,
	};
}
